import { nanoid } from 'nanoid';
import type { TableFieldConfig, TableRowValue } from '@shared/types/interactiveFields';

type Language = 'bg' | 'de' | 'en';

interface TableFieldProps {
  field: TableFieldConfig;
  value: TableRowValue[];
  onChange: (value: TableRowValue[]) => void;
  error?: string;
  disabled?: boolean;
  language: Language;
}

export function TableField({ field, value, onChange, error, disabled, language }: TableFieldProps) {
  const rows = value || [];

  const addRow = () => {
    if (field.maxRows && rows.length >= field.maxRows) return;

    const newRow: TableRowValue = {
      rowId: nanoid(),
      values: {},
    };
    onChange([...rows, newRow]);
  };

  const removeRow = (rowId: string) => {
    if (field.minRows && rows.length <= field.minRows) return;
    onChange(rows.filter((r) => r.rowId !== rowId));
  };

  const updateCell = (rowId: string, columnId: string, cellValue: string) => {
    onChange(
      rows.map((row) =>
        row.rowId === rowId
          ? { ...row, values: { ...row.values, [columnId]: cellValue } }
          : row
      )
    );
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {field.label[language] || field.label.bg}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Desktop: Table view */}
      <div className="hidden md:block overflow-x-auto border border-gray-300 rounded-lg">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              {field.columns.map((col) => (
                <th key={col.id} className="border-b border-gray-300 p-2 text-left text-sm font-medium text-gray-700">
                  {col.header[language] || col.header.bg}
                  {col.required && <span className="text-red-500 ml-1">*</span>}
                </th>
              ))}
              {field.allowRemoveRow && <th className="border-b border-gray-300 p-2 w-10"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowId} className="hover:bg-gray-50">
                {field.columns.map((col) => (
                  <td key={col.id} className="border-b border-gray-200 p-1">
                    <input
                      type={col.type === 'tel' ? 'tel' : col.type === 'email' ? 'email' : col.type === 'date' ? 'date' : 'text'}
                      value={row.values[col.id] || ''}
                      onChange={(e) => updateCell(row.rowId, col.id, e.target.value)}
                      placeholder={col.placeholder}
                      disabled={disabled}
                      className="w-full px-2 py-1.5 text-sm border-0 bg-transparent focus:ring-2 focus:ring-blue-500 rounded"
                    />
                  </td>
                ))}
                {field.allowRemoveRow && rows.length > (field.minRows || 0) && (
                  <td className="border-b border-gray-200 p-1 text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(row.rowId)}
                      disabled={disabled}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Изтрий ред"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: Card view */}
      <div className="md:hidden space-y-4">
        {rows.map((row, rowIndex) => (
          <div key={row.rowId} className="border border-gray-300 rounded-lg p-4 bg-white">
            <div className="flex justify-between items-center mb-3">
              <span className="font-medium text-gray-900">
                {field.rowLabel?.[language] || field.rowLabel?.bg || 'Ред'} {rowIndex + 1}
              </span>
              {field.allowRemoveRow && rows.length > (field.minRows || 0) && (
                <button
                  type="button"
                  onClick={() => removeRow(row.rowId)}
                  disabled={disabled}
                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Изтрий ред"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="space-y-3">
              {field.columns.map((col) => (
                <div key={col.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {col.header[language] || col.header.bg}
                    {col.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={col.type === 'tel' ? 'tel' : col.type === 'email' ? 'email' : col.type === 'date' ? 'date' : 'text'}
                    value={row.values[col.id] || ''}
                    onChange={(e) => updateCell(row.rowId, col.id, e.target.value)}
                    placeholder={col.placeholder}
                    disabled={disabled}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Add row button */}
      {field.allowAddRow && (!field.maxRows || rows.length < field.maxRows) && (
        <button
          type="button"
          onClick={addRow}
          disabled={disabled}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Добави {field.rowLabel?.[language] || field.rowLabel?.bg || 'ред'}
        </button>
      )}

      {/* Row count indicator */}
      {field.maxRows && (
        <p className="text-sm text-gray-500">
          {rows.length} от {field.maxRows}
        </p>
      )}

      {/* Help text */}
      {field.helpText && (
        <p className="text-sm text-gray-500">
          {field.helpText[language] || field.helpText.bg}
        </p>
      )}

      {/* Error */}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
}
